import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Car, Copy, ExternalLink, Loader2, RefreshCw, ShieldCheck, Wrench,
  CheckCircle2, Clock, AlertTriangle, Eye, ChevronDown, ChevronRight,
} from 'lucide-react';

const TONES = {
  amber: { pill: 'bg-amber-100 text-amber-800', bar: 'bg-amber-400', card: 'border-amber-200 bg-amber-50/40' },
  blue: { pill: 'bg-blue-100 text-blue-700', bar: 'bg-blue-400', card: 'border-slate-200 bg-white' },
  indigo: { pill: 'bg-indigo-100 text-indigo-700', bar: 'bg-indigo-400', card: 'border-slate-200 bg-white' },
  violet: { pill: 'bg-violet-100 text-violet-700', bar: 'bg-violet-400', card: 'border-slate-200 bg-white' },
  yellow: { pill: 'bg-yellow-100 text-yellow-800', bar: 'bg-yellow-400', card: 'border-slate-200 bg-white' },
  green: { pill: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-400', card: 'border-emerald-200 bg-emerald-50/40' },
  orange: { pill: 'bg-orange-100 text-orange-800', bar: 'bg-orange-400', card: 'border-orange-200 bg-orange-50/40' },
  slate: { pill: 'bg-slate-100 text-slate-600', bar: 'bg-slate-300', card: 'border-slate-200 bg-slate-50/60' },
};

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const fmtSlot = (date, time) => {
  if (!date) return '';
  const d = new Date(`${date}T00:00:00`);
  const day = Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return time ? `${day}, ${time}` : day;
};

const waMobile = (mobile) => {
  const digits = String(mobile || '').replace(/\D/g, '');
  const last10 = digits.slice(-10);
  return last10.length === 10 ? `91${last10}` : '';
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Older browsers / non-HTTPS contexts deny the async clipboard API.
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  }
};

const CarCard = ({ car, total, customerName, customerMobile, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const tone = TONES[car.tone] || TONES.slate;
  const waitingOnRep = car.waiting_on === 'sales';
  const report = car.report;

  const copyLink = async () => {
    const ok = await copyToClipboard(report.url);
    if (ok) toast.success('Report link copied — paste it in the customer\'s WhatsApp chat');
    else toast.error('Could not copy — select the link above and copy it manually');
  };

  const waNumber = waMobile(customerMobile);
  const shareOnWhatsApp = () => {
    const text = `Hi ${customerName || 'there'}, your Wisedrive inspection report${
      car.car_number ? ` for ${car.car_number}` : ''
    } is ready.\n\n${report.url}\n\nOpen the link and verify with the OTP sent to your number to view the full report.`;
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div
      className={`rounded-xl border ${tone.card} overflow-hidden`}
      data-testid={`inspection-car-card-${car.inspection_id}`}
    >
      <div className="flex">
        <div className={`w-1 flex-shrink-0 ${tone.bar}`} />
        <div className="flex-1 min-w-0 p-3.5">
          {/* stage + slot */}
          <div className="flex items-start justify-between gap-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${tone.pill}`}
              data-testid={`inspection-stage-${car.inspection_id}`}
            >
              {car.stage === 'report_ready' ? <CheckCircle2 className="h-3 w-3" />
                : waitingOnRep ? <AlertTriangle className="h-3 w-3" />
                  : <Clock className="h-3 w-3" />}
              {car.label}
              {car.age_label && !['report_ready', 'cancelled', 'refunded'].includes(car.stage) && (
                <span className="font-normal opacity-75">· {car.age_label}</span>
              )}
            </span>
            {total > 1 && (
              <span className="text-[11px] text-slate-400 whitespace-nowrap">Car {car.slot} of {total}</span>
            )}
          </div>

          {/* car identity */}
          <div className="mt-2.5 flex items-center gap-2 flex-wrap">
            {car.car_number ? (
              <span className="px-2 py-0.5 rounded bg-slate-900 text-white font-mono text-xs tracking-wider">
                {car.car_number}
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded border border-dashed border-amber-400 text-amber-700 text-xs font-medium">
                Car number not shared yet
              </span>
            )}
            {car.car && <span className="text-sm font-medium text-slate-800">{car.car}</span>}
          </div>

          {/* ops facts */}
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500">
            {car.scheduled_date && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> Visit {fmtSlot(car.scheduled_date, car.scheduled_time)}
              </span>
            )}
            {car.mechanic_name && (
              <span className="flex items-center gap-1 truncate" title={car.mechanic_name}>
                <Wrench className="h-3 w-3" /> {car.mechanic_name}
              </span>
            )}
            {car.package_name && <span className="truncate" title={car.package_name}>{car.package_name}</span>}
            {car.city && <span className="truncate">{car.city}</span>}
          </div>

          {/* what happens next */}
          <div
            className={`mt-2.5 px-2.5 py-2 rounded-lg text-[12px] leading-snug ${
              waitingOnRep ? 'bg-amber-100/70 text-amber-900' : 'bg-slate-100/80 text-slate-600'
            }`}
            data-testid={`inspection-next-step-${car.inspection_id}`}
          >
            <span className="font-semibold">
              {waitingOnRep ? 'Action needed from you: ' : 'What happens next: '}
            </span>
            {car.next_step}
          </div>

          {/* report link */}
          {car.report_link_missing && (
            <div className="mt-2.5 px-2.5 py-2 rounded-lg bg-amber-100/70 text-[12px] text-amber-900"
                 data-testid={`inspection-report-missing-${car.inspection_id}`}>
              Report is published but its link is missing on this record — ask the inspections team to
              re-publish it.
            </div>
          )}
          {report && (
            <div className="mt-2.5 p-2.5 rounded-lg bg-white border border-emerald-200" data-testid={`inspection-report-${car.inspection_id}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
                <ShieldCheck className="h-3.5 w-3.5" />
                Report link (customer verifies with their own OTP)
              </div>
              <div className="mt-1.5 px-2 py-1.5 rounded bg-slate-50 border text-[11px] font-mono text-slate-600 break-all">
                {report.url}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  onClick={copyLink}
                  className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-xs font-medium flex items-center gap-1 hover:bg-slate-800"
                  data-testid={`copy-report-link-${car.inspection_id}`}
                >
                  <Copy className="h-3 w-3" /> Copy link
                </button>
                <button
                  onClick={shareOnWhatsApp}
                  disabled={!waNumber}
                  title={waNumber ? 'Open the customer\'s WhatsApp chat with the link ready to send'
                    : 'Customer mobile number looks incomplete — copy the link instead'}
                  className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-xs font-medium flex items-center gap-1 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`share-report-whatsapp-${car.inspection_id}`}
                >
                  <ExternalLink className="h-3 w-3" /> Share on WhatsApp
                </button>
              </div>
              <div className="mt-2 space-y-0.5 text-[11px] text-slate-500">
                <div>Published {fmt(report.published_at)}{report.published_by ? ` by ${report.published_by}` : ''}</div>
                {report.whatsapp_sent && <div>Already sent to the customer on WhatsApp</div>}
                {report.customer_viewed_at && (
                  <div className="flex items-center gap-1 text-emerald-700 font-medium">
                    <Eye className="h-3 w-3" /> Customer opened the report {fmt(report.customer_viewed_at)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* timeline */}
          {car.timeline?.length > 0 && (
            <>
              <button
                onClick={() => setOpen(!open)}
                className="mt-2.5 flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700"
                data-testid={`inspection-timeline-toggle-${car.inspection_id}`}
              >
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                {open ? 'Hide' : 'Show'} full history ({car.timeline.length})
              </button>
              {open && (
                <div className="mt-2 pl-1" data-testid={`inspection-timeline-${car.inspection_id}`}>
                  {car.timeline.map((step, idx) => (
                    <div key={`${step.key}-${idx}`} className="relative pl-4 pb-2 last:pb-0 border-l border-slate-200 last:border-l-0">
                      <span className="absolute -left-[3px] top-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                      <div className="text-[12px] text-slate-700 leading-snug">{step.label}</div>
                      <div className="text-[10px] text-slate-400">
                        {fmt(step.at)}{step.by ? ` · ${step.by}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Read-only inspection tracking for a sales rep: one card per car, newest stage first. */
export const InspectionStatusTab = ({ progress, loading, onRefresh, refreshing }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 gap-2" data-testid="inspection-status-loading">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> Loading inspection status...
      </div>
    );
  }

  const cars = progress?.cars || [];
  if (!cars.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="inspection-status-empty">
        <Car className="h-10 w-10 text-slate-300 mb-2" />
        <p className="text-sm font-medium text-slate-600">No inspection yet</p>
        <p className="text-xs text-slate-400 mt-1 max-w-[280px]">
          An inspection is created the moment the customer pays. Once that happens, every stage up to
          the report link shows here.
        </p>
      </div>
    );
  }

  const ready = progress?.badge?.ready || 0;
  const total = progress?.badge?.total || cars.length;

  return (
    <div className="h-full overflow-y-auto px-6 pb-6" data-testid="inspection-status-tab">
      <div className="sticky top-0 -mx-6 px-6 py-2.5 bg-white/95 backdrop-blur border-b flex items-center justify-between z-10">
        <div className="text-xs text-slate-600">
          <span className="font-semibold text-slate-800">{total} car{total > 1 ? 's' : ''}</span>
          {' · '}
          <span className={ready === total ? 'text-emerald-600 font-medium' : ''}>
            {ready} report{ready === 1 ? '' : 's'} ready
          </span>
          {progress?.slots_purchased > total && (
            <span className="text-slate-400"> · {progress.slots_purchased} paid for</span>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          title="Refresh"
          data-testid="inspection-status-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-3 pt-3">
        {cars.map((car, idx) => (
          <CarCard
            key={car.inspection_id}
            car={car}
            total={total}
            customerName={progress?.customer_name}
            customerMobile={progress?.customer_mobile}
            defaultOpen={cars.length === 1 || idx === 0}
          />
        ))}
      </div>

      <p className="mt-4 text-[11px] text-slate-400 leading-snug">
        Read-only view of what the inspections team sees. The report link always asks the customer for
        an OTP on their own number, so it is safe to forward.
      </p>
    </div>
  );
};

export default InspectionStatusTab;
